import { useRef, useState } from 'react';
import {
  addCurvePoint,
  curveTable,
  findCurvePoint,
  moveCurvePoint,
  removeCurvePoint,
} from '../../adjustments/curves';
import { clamp } from '../../core/math';
import type { Curve, CurveChannels, Point } from '../../core/types';

type ChannelKey = keyof CurveChannels;

const CHANNELS: { key: ChannelKey; label: string; stroke: string }[] = [
  { key: 'rgb', label: 'RGB', stroke: 'var(--text)' },
  { key: 'r', label: 'Red', stroke: '#e05a5a' },
  { key: 'g', label: 'Green', stroke: '#5ac46a' },
  { key: 'b', label: 'Blue', stroke: '#5a86e0' },
];

/** How close, in curve units, the pointer has to be to grab a point. */
const GRAB = 0.04;

function pathFor(curve: Curve, samples = 64): string {
  const table = curveTable(curve, samples);
  return table
    .reduce<string[]>((parts, value, index) => {
      const x = index / (samples - 1);
      parts.push(`${index === 0 ? 'M' : 'L'} ${x.toFixed(4)} ${(1 - value).toFixed(4)}`);
      return parts;
    }, [])
    .join(' ');
}

export interface CurveEditorProps {
  curves: CurveChannels;
  onChange: (curves: CurveChannels) => void;
  /** 256 counts per channel drawn behind the curve, when the caller has them. */
  histogram?: Float32Array | null;
  size?: number;
}

/**
 * The Curves grid. Dragging a point reshapes the tone response, clicking empty
 * space adds a point and alt-clicking one removes it, as in Photoshop.
 */
export function CurveEditor({ curves, onChange, histogram, size = 236 }: CurveEditorProps) {
  const [channel, setChannel] = useState<ChannelKey>('rgb');
  const [dragging, setDragging] = useState<number | null>(null);
  const surface = useRef<SVGSVGElement | null>(null);
  const curve = curves[channel];

  const pointFrom = (event: React.PointerEvent): Point => {
    const box = surface.current?.getBoundingClientRect();
    if (!box) return { x: 0, y: 0 };
    return {
      x: clamp((event.clientX - box.left) / box.width, 0, 1),
      y: clamp(1 - (event.clientY - box.top) / box.height, 0, 1),
    };
  };

  const emit = (next: Curve) => onChange({ ...curves, [channel]: next });

  const onPointerDown = (event: React.PointerEvent) => {
    const at = pointFrom(event);
    const hit = findCurvePoint(curve, at, GRAB);

    if (event.altKey) {
      if (hit >= 0) emit(removeCurvePoint(curve, hit));
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    if (hit >= 0) {
      setDragging(hit);
      return;
    }
    const added = addCurvePoint(curve, at);
    setDragging(added.index);
    emit(added.curve);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (dragging == null) return;
    const moved = moveCurvePoint(curve, dragging, pointFrom(event));
    // The point can swap places with a neighbour, so follow it by position.
    const at = pointFrom(event);
    setDragging(findCurvePoint(moved, { x: at.x, y: clamp(at.y, 0, 1) }, 1));
    emit(moved);
  };

  const stop = () => setDragging(null);

  const histogramPath = (): string | null => {
    if (!histogram || histogram.length === 0) return null;
    let peak = 0;
    for (const value of histogram) peak = Math.max(peak, value);
    if (peak <= 0) return null;
    const parts = ['M 0 1'];
    for (let i = 0; i < histogram.length; i += 1) {
      const x = i / (histogram.length - 1);
      parts.push(`L ${x.toFixed(4)} ${(1 - histogram[i] / peak).toFixed(4)}`);
    }
    parts.push('L 1 1 Z');
    return parts.join(' ');
  };

  const stroke = CHANNELS.find((item) => item.key === channel)?.stroke ?? 'var(--text)';
  const bars = histogramPath();

  return (
    <div className="curve-editor">
      <div className="curve-editor__tabs">
        {CHANNELS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`curve-editor__tab${channel === item.key ? ' is-active' : ''}`}
            onClick={() => setChannel(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <svg
        ref={surface}
        className="curve-editor__grid"
        width={size}
        height={size}
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stop}
        onPointerCancel={stop}
      >
        <rect x={0} y={0} width={1} height={1} className="curve-editor__field" />
        {bars && <path d={bars} className="curve-editor__histogram" />}
        {[0.25, 0.5, 0.75].map((at) => (
          <g key={at}>
            <line x1={at} y1={0} x2={at} y2={1} className="curve-editor__rule" />
            <line x1={0} y1={at} x2={1} y2={at} className="curve-editor__rule" />
          </g>
        ))}
        <line x1={0} y1={1} x2={1} y2={0} className="curve-editor__diagonal" />
        <path d={pathFor(curve)} className="curve-editor__curve" style={{ stroke }} />
        {curve.map((point, index) => (
          <circle
            key={`${index}-${point.x}`}
            cx={point.x}
            cy={1 - point.y}
            r={0.022}
            className={`curve-editor__knot${dragging === index ? ' is-active' : ''}`}
          />
        ))}
      </svg>
      <p className="curve-editor__hint">Click to add a point, Alt-click to remove it.</p>
    </div>
  );
}
